// Main facade — pure functions that orchestrate the export flow
// This module imports and re-exports all the helper functions for export, so that the main exportGrid function can be clean and focused on orchestration.

import { validateBeforeExport } from './validate.js';
import { cloneColumnsDeep } from './cloneColumns.js';
import { flattenColumns } from './flattenColumns.js';
import { syncVisibility } from './syncVisibility.js';
import { processExportFields } from './processExportFields.js';
import { exportAsPdf } from './pdfExporter.js';
import { exportAsExcel } from './excelExporter.js';
import { restoreGrid } from './restoreGrid.js';
import { formatCellValue } from './cellFormatter.js';


export async function exportGrid(e, fileName, originalColumns, options = {}) {
    if (!validateBeforeExport(e, options)) return;

    // e.component is the dxDataGrid instance
    const grid = e.component;

    // compute visible flat columns
    const visibleFlat = flattenColumns(options.visibleColumns || grid.getVisibleColumns());

    // Save state before any mutation
    const currentGridState = grid.state ? grid.state() : null;

    // Clone for restoration — taken before any export mutations
    const beforeExportColumns = cloneColumnsDeep(originalColumns);
    syncVisibility(beforeExportColumns, visibleFlat);

    // Clone for export — never mutate originalColumns directly (it's a reactive computed reference)
    const columnsForExport = cloneColumnsDeep(originalColumns);
    syncVisibility(columnsForExport, visibleFlat);



    // process export fields (may reveal hidden fields for export)
    const columnNames = [];
    processExportFields(columnsForExport, columnNames, options.persistenceKey, options.storageGetter);

    // set columns on component (DevExtreme will accept structured columns)
    const finalColumnsForExport = columnsForExport.filter((c) => c.caption || c.columns);

    e.component.beginUpdate();
    e.component.option('columns', finalColumnsForExport);

    try {
        if (e.format === 'pdf') {
            await exportAsPdf({ e, fileName, grid, finalColumnsForExport, formatCellValue, options });
        } else {
            await exportAsExcel({ e, fileName, grid, finalColumnsForExport, formatCellValue, options });
        }
    }
    catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error during export:", err);
    }
    finally {
        // ensure restoration even if export fails
        await restoreGrid({
            e,
            grid,
            currentGridState,
            beforeExportColumns: beforeExportColumns,
            columnNames,
            persistenceKey: options.persistenceKey,
            storageGetter: options.storageGetter,  // <-- REQUIRED
            finalFilterValues: options.finalFilterValues,
            visibleColumns: options.visibleColumns,
        });
        e.component.endUpdate();
    }
}